/**
 * Modern UI Demo Page
 * 
 * Showcases all the modern UI components in a complete application layout
 */

import React, { useState } from 'react';
import { ThemeProvider, CssBaseline, Box } from '@mui/material';
import { lightTheme } from '../../theme';
import ModernSidebar from '../organisms/ModernSidebar';
import ModernAppBar from '../organisms/ModernAppBar';
import ModernWorkspace from './ModernWorkspace';
import FloatingToolbar from '../organisms/FloatingToolbar';

const ModernUIDemo: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [selectedTool, setSelectedTool] = useState('select');
  const [projectName] = useState('Conference Room Design');
  const [isSaved, setIsSaved] = useState(true);
  const [canUndo] = useState(true);
  const [canRedo] = useState(false);

  const handleSave = () => {
    setIsSaved(true);
    console.log('Project saved');
  };

  const handleExport = () => {
    console.log('Exporting project...');
  };

  const handleShare = () => {
    console.log('Sharing project...');
  };

  const handleUndo = () => {
    console.log('Undo action');
  };

  const handleRedo = () => {
    console.log('Redo action');
  };

  const handleSearch = (query: string) => {
    console.log('Searching for:', query);
  };

  const handleToolSelect = (tool: string) => {
    setSelectedTool(tool);
    console.log('Tool selected:', tool);
  };

  return (
    <ThemeProvider theme={lightTheme}>
      <CssBaseline />
      <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
        {/* App Bar */}
        <ModernAppBar
          projectName={projectName}
          isSaved={isSaved}
          onSave={handleSave}
          onExport={handleExport}
          onShare={handleShare}
          onUndo={handleUndo}
          onRedo={handleRedo}
          canUndo={canUndo}
          canRedo={canRedo}
          onSearch={handleSearch}
        />

        {/* Sidebar */}
        <ModernSidebar
          open={sidebarOpen}
          onToggle={() => setSidebarOpen(!sidebarOpen)}
          selectedTool={selectedTool}
          onToolSelect={handleToolSelect}
        />

        {/* Main Workspace */}
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            marginLeft: sidebarOpen ? '280px' : '72px',
            transition: 'margin-left 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms',
          }}
        >
          <ModernWorkspace>
            {/* Canvas content would go here */}
            <Box
              sx={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                position: 'relative',
              }}
            >
              {/* This is where the actual room designer canvas would be rendered */}
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 800 600"
                style={{ maxWidth: '100%', maxHeight: '100%' }}
              >
                {/* Grid */}
                <defs>
                  <pattern
                    id="grid"
                    width="20"
                    height="20"
                    patternUnits="userSpaceOnUse"
                  >
                    <path
                      d="M 20 0 L 0 0 0 20"
                      fill="none"
                      stroke="#e0e0e0"
                      strokeWidth="0.5"
                    />
                  </pattern>
                </defs>
                <rect width="100%" height="100%" fill="url(#grid)" />

                {/* Sample Room */}
                <rect
                  x="100"
                  y="100"
                  width="600"
                  height="400"
                  fill="none"
                  stroke="#333"
                  strokeWidth="2"
                />

                {/* Sample Furniture */}
                <rect
                  x="150"
                  y="200"
                  width="200"
                  height="100"
                  fill="#f5f5f5"
                  stroke="#666"
                  strokeWidth="1"
                />
                <text x="250" y="250" textAnchor="middle" fill="#666">
                  Conference Table
                </text>

                {/* Sample Speakers */}
                <circle cx="120" cy="120" r="15" fill="#1863D3" opacity="0.8" />
                <circle cx="680" cy="120" r="15" fill="#1863D3" opacity="0.8" />
                <circle cx="120" cy="480" r="15" fill="#1863D3" opacity="0.8" />
                <circle cx="680" cy="480" r="15" fill="#1863D3" opacity="0.8" />

                {/* Coverage visualization */}
                <circle
                  cx="120"
                  cy="120"
                  r="100"
                  fill="#1863D3"
                  opacity="0.1"
                />
                <circle
                  cx="680"
                  cy="120"
                  r="100"
                  fill="#1863D3"
                  opacity="0.1"
                />
                <circle
                  cx="120"
                  cy="480"
                  r="100"
                  fill="#1863D3"
                  opacity="0.1"
                />
                <circle
                  cx="680"
                  cy="480"
                  r="100"
                  fill="#1863D3"
                  opacity="0.1"
                />
              </svg>
            </Box>
          </ModernWorkspace>
        </Box>

        {/* Floating Toolbar */}
        <FloatingToolbar
          selectedTool={selectedTool}
          onToolSelect={handleToolSelect}
          position="left"
        />
      </Box>
    </ThemeProvider>
  );
};

export default ModernUIDemo;